import React, { useEffect, useState } from "react";
import ConversationList from "../components/chatting/conversation/ConversationList";
import ChatWindow from "../components/chatting/chat_messages/ChatWindow";
import { Icon } from "@iconify/react";
import Loader from "../components/loading/Loader";
import { get_conversations_api } from "../DAL/Conversations";
import toast from "react-hot-toast";
import ChatDrawer from "../components/chatting/chat_messages/ChatDrawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAuth } from "../hooks/useAuth";
import { get_messages_api } from "../DAL/Messages";
import { scroll_to_bottom_on_message } from "../utils/constant";

import {
  handleUpdateMessage,
  handleDeleteMessage,
  handleNewMessage,
} from "../utils/socketHandlers";

export default function Chatting() {
  const { userInfo, socket } = useAuth();
  const [selectedChat, setSelectedChat] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [isLoadingMesssages, setIsLoadingMesssages] = useState(false);
  const [isSubmittingMsg, setIsSubmittingMsg] = useState(false);
  const [searchText, setSearchText] = useState("");

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const getConversationsList = async () => {
    setIsLoadingChats(true);
    const result = await get_conversations_api(searchText);
    if (result.code === 200) {
      const conversationUsers = result.conversations.map((c) => {
        if (c.type === "direct" && c.participants.length > 0) {
          let findReceiver = c.participants.find(
            ({ id }) => id !== userInfo.id
          );
          c = { ...c, ...findReceiver };
        }
        return c;
      });
      setConversations([...conversationUsers, ...result.users]);
      setIsLoading(false);
      setIsLoadingChats(false);
    } else {
      toast.error(`❌ ${result.message}`);
      setIsLoading(false);
      setIsLoadingChats(false);
    }
  };

  const getMessagesList = async () => {
    setIsLoadingMesssages(true);
    const result = await get_messages_api(selectedChat.conversation_id);
    if (result.code === 200) {
      setMessages(result.messages);
      setIsLoadingMesssages(false);
      scroll_to_bottom_on_message(true);
    } else {
      toast.error(`❌ ${result.message}`);
      setIsLoadingMesssages(false);
    }
  };

  const handleCross = () => {
    setSelectedChat(null);
  };

  useEffect(() => {
    setMessages([]);
    if (selectedChat?.conversation_id) {
      getMessagesList();
    }
    if (!socket) return;
    // Attach socket handlers
    const newMessageHandler = handleNewMessage({
      userInfo,
      selectedChat,
      setSelectedChat,
      setConversations,
      setMessages,
      setIsSubmittingMsg,
    });

    const updateMessageHandler = handleUpdateMessage({
      setMessages,
      setIsSubmittingMsg,
      userInfo,
      setConversations,
    });

    const deleteMessageHandler = handleDeleteMessage({ setMessages });

    socket.on("new_message_receiver", newMessageHandler);
    socket.on("update_message_receiver", updateMessageHandler);
    socket.on("delete_message_receiver", deleteMessageHandler);

    return () => {
      socket.off("new_message_receiver", newMessageHandler);
      socket.off("update_message_receiver", updateMessageHandler);
      socket.off("delete_message_receiver", deleteMessageHandler);
    };
  }, [selectedChat, socket]);

  useEffect(() => {
    getConversationsList();
  }, [searchText]);

  if (isLoading) return <Loader />;

  return (
    <>
      <div className="container-fluid chatting-page py-2">
        <div className="row h-100">
          <div className="col-md-4">
            <div className="theme-card h-100">
              <ConversationList
                {...{
                  conversations,
                  selectedChat,
                  setSelectedChat,
                  searchText,
                  setSearchText,
                  isLoadingChats,
                }}
              />
            </div>
          </div>

          {isDesktop && (
            <div className="col-md-8 d-flex justify-content-center align-items-center pt-0">
              <div className="theme-card h-100 w-100 p-0 position-relative">
                {selectedChat ? (
                  <ChatWindow
                    {...{
                      isSubmittingMsg,
                      setIsSubmittingMsg,
                      isLoadingMesssages,
                      messages,
                      handleCross,
                      chat: selectedChat,
                    }}
                  />
                ) : (
                  <div className="text-center p-5 no-chat-selected">
                    <Icon icon="uiw:message" width="70" className="mb-4" />
                    <h2 className="mb-3">Cloudfish Messaging</h2>
                    <div>
                      Stay connected with your contacts instantly. <br />
                      Select a conversation from the left panel to start
                      chatting.
                      <br />
                      You can send messages, images, and attachments securely.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {!isDesktop && (
        <ChatDrawer open={!!selectedChat} onClose={handleCross}>
          {selectedChat && (
            <ChatWindow
              {...{
                isSubmittingMsg,
                setIsSubmittingMsg,
                isLoadingMesssages,
                messages,
                handleCross,
                chat: selectedChat,
              }}
            />
          )}
        </ChatDrawer>
      )}
    </>
  );
}
